import { Fragment, useContext, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon, DownloadIcon, PlayIcon, PhotographIcon, PencilIcon } from '@heroicons/react/outline';
import { MusicSlideOverContext } from 'context/MusicSlideOverContext';
import { MusicPlayerContext } from 'context/MusicPlayer';
import icon from 'assets/images/music-icon.svg';
import icon2 from 'assets/images/music-icon.png';
import spotify from 'assets/images/brands/spotify.svg';
import deezer from 'assets/images/brands/deezer.svg';
import youtube from 'assets/images/brands/youtube.svg';
import { useUtils } from 'hooks/UtilityHook';
import { NavLink } from 'react-router-dom';
import { LibrarySlideOverContext } from 'context/LibrarySlideOverContext';
import { ApiConsumer } from 'api/ApiConsumer';
import { API_ROUTES } from 'routes/api';

export default function SlideDetail() {
  let { music, setMusic } = useContext(MusicSlideOverContext);
  let { setLibraryItem } = useContext(LibrarySlideOverContext);
  let { setPlaying } = useContext(MusicPlayerContext);
  let { downloadFile } = useUtils();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const isPlaceholder = !music.largeAlbumCover && !music.albumCover && !music.artwork;

  const handleArtworkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !music?.id) return;

    setUploading(true);
    const data = new FormData();
    data.append('artwork', file);

    try {
      const res = await ApiConsumer.put(
        `${API_ROUTES.LIBRARY.ALL}external/items/${music.id}`,
        data,
      );
      const updatedArtwork = res?.data?.artwork;
      if (updatedArtwork) {
        setMusic({ ...music, artwork: updatedArtwork });
      }
    } catch (err) {
      console.error('artwork upload error', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Transition.Root show={music?.open ?? false} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden z-50"
        onClose={() => setMusic({ open: false, user: {} })}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="pointer-events-auto relative w-96">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-500"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-500"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4">
                    <button
                      type="button"
                      className="rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                      onClick={() => setMusic({ open: false })}
                    >
                      <span className="sr-only">Close panel</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="h-full overflow-y-auto bg-white p-8">
                  <div className="space-y-6 pb-16">
                    <div>
                      <dl className="mt-2">
                        <div className="py-3">
                          <div className="relative group w-full overflow-hidden rounded-xl shadow-sm ring-1 ring-black/5">
                            <img
                              className="h-auto w-full block"
                              src={
                                music.artwork || music.largeAlbumCover || music.albumCover || icon2
                              }
                              alt="album cover"
                            />
                            {music?.can_edit ? (
                              <>
                                {/* Whole cover is the click target; gradient prompt on hover/focus */}
                                <button
                                  type="button"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={uploading}
                                  title={
                                    isPlaceholder ? 'Upload artwork' : 'Change artwork'
                                  }
                                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 cursor-pointer bg-gradient-to-t from-black/75 via-black/20 to-transparent opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-200 focus:outline-none"
                                >
                                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/50 backdrop-blur-sm">
                                    <PhotographIcon className="h-6 w-6 text-white" />
                                  </span>
                                  <span className="text-white text-sm font-semibold drop-shadow">
                                    {isPlaceholder ? 'Upload artwork' : 'Change artwork'}
                                  </span>
                                </button>

                                {/* Resting-state hint — fades out as the hover prompt fades in */}
                                <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur-sm pl-2.5 pr-3 py-1.5 text-xs font-semibold text-gray-700 shadow-lg ring-1 ring-black/5 opacity-100 group-hover:opacity-0 transition-opacity duration-200">
                                  <PencilIcon className="h-3.5 w-3.5 text-blue-600" />
                                  {isPlaceholder ? 'Add artwork' : 'Edit'}
                                </span>

                                {/* Upload progress state */}
                                {uploading ? (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60">
                                    <svg
                                      className="h-7 w-7 animate-spin text-white"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      />
                                      <path
                                        className="opacity-90"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                      />
                                    </svg>
                                    <span className="text-white text-sm font-medium">
                                      Uploading…
                                    </span>
                                  </div>
                                ) : null}

                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.gif"
                                  className="hidden"
                                  onChange={handleArtworkUpload}
                                />
                              </>
                            ) : null}
                          </div>
                        </div>

                        {music.acrid ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">ID</dt>
                            <dd className="text-gray-900">{music.acrid}</dd>
                          </div>
                        ) : null}
                        {music.title ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Title</dt>
                            <dd className="text-gray-900">{music.title}</dd>
                          </div>
                        ) : null}
                        {music.artist ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Artist</dt>
                            <dd className="text-gray-900">{music.artist}</dd>
                          </div>
                        ) : null}
                        {music.composer ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Composer</dt>
                            <dd className="text-gray-900">{music.composer}</dd>
                          </div>
                        ) : null}
                        {music.album ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Album</dt>
                            <dd className="text-gray-900">{music.album}</dd>
                          </div>
                        ) : null}
                        {music.label ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Label</dt>
                            <dd className="text-gray-900">{music.label}</dd>
                          </div>
                        ) : null}
                        {music.music_type_code ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Music Type Code</dt>
                            <dd className="text-gray-900">{music.music_type_code}</dd>
                          </div>
                        ) : null}
                        {music.performer ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Performer</dt>
                            <dd className="text-gray-900">{music.performer}</dd>
                          </div>
                        ) : null}
                        {music.publisher ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Publisher</dt>
                            <dd className="text-gray-900">{music.publisher}</dd>
                          </div>
                        ) : null}
                        {music.duration ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Duration</dt>
                            <dd className="text-gray-900">{music.duration}</dd>
                          </div>
                        ) : null}
                        {music?.isrc ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">ISRC</dt>
                            <dd className="text-gray-900">
                              {music?.isrc ?? '-'}
                            </dd>
                          </div>
                        ) : null}
                        {music?.iswc ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">ISWC</dt>
                            <dd className="text-gray-900">
                              {music?.iswc ?? '-'}
                            </dd>
                          </div>
                        ) : null}
                        {music?.upc ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">UPC</dt>
                            <dd className="text-gray-900">
                              {music?.upc ?? '-'}
                            </dd>
                          </div>
                        ) : null}
                        {music?.release_year ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Release Year</dt>
                            <dd className="text-gray-900">
                              {music?.release_year}
                            </dd>
                          </div>
                        ) : null}
                        {music?.uploaded_by ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Uploaded By</dt>
                            <dd className="text-gray-900">
                              {`${music?.uploaded_by?.first_name} ${music?.uploaded_by?.last_name}`}
                            </dd>
                          </div>
                        ) : null}
                        {music?.country_produced ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Country Produced</dt>
                            <dd className="text-gray-900">
                              {`${music?.country_produced}`}
                            </dd>
                          </div>
                        ) : null}
                        {music?.language ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Language</dt>
                            <dd className="text-gray-900">{`${music?.language}`}</dd>
                          </div>
                        ) : null}
                        {music?.acr_recording_type_label ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Recording Type Label</dt>
                            <dd className="text-gray-900">{`${music?.acr_recording_type_label ?? "-"}`}</dd>
                          </div>
                        ) : null}
                        {music?.timestamp ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Timestamp</dt>
                            <dd className="text-gray-900">
                              {music?.timestamp?.replace('T', ' ')}
                            </dd>
                          </div>
                        ) : null}
                        {music?.created ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Created</dt>
                            <dd className="text-gray-900">
                              {music?.created?.replace('T', ' ')}
                            </dd>
                          </div>
                        ) : null}
                        {music?.updated ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Last Updated</dt>
                            <dd className="text-gray-900">
                              {music?.updated?.replace('T', ' ')}
                            </dd>
                          </div>
                        ) : null}
                        {music?.count ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Play Count</dt>
                            <dd className="text-gray-900">{music?.count}</dd>
                          </div>
                        ) : null}
                        {music?.play_duration ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Total Duration</dt>
                            <dd className="text-gray-900">
                              {music?.play_duration}
                            </dd>
                          </div>
                        ) : null}
                        {music?.spotify_id ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Spotify</dt>
                            <dd className="text-gray-900">
                              <a
                                href={`https://open.spotify.com/track/${music?.spotify_id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  className="h-5 text-right cursor-pointer"
                                  src={spotify}
                                  alt=""
                                />
                              </a>
                            </dd>
                          </div>
                        ) : null}
                        {music?.deezer_id ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Deezer</dt>
                            <dd className="text-gray-900">
                              <a
                                href={`https://www.deezer.com/us/track/${music?.deezer_id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  className="h-5 text-right cursor-pointer"
                                  src={deezer}
                                  alt=""
                                />
                              </a>
                            </dd>
                          </div>
                        ) : null}
                        {music?.youtube_vid ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Youtube</dt>
                            <dd className="text-gray-900">
                              <a
                                href={`https://www.youtube.com/watch?v=${music?.youtube_vid}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  className="h-5 text-right cursor-pointer"
                                  src={youtube}
                                  alt=""
                                />
                              </a>
                            </dd>
                          </div>
                        ) : null}
                        {music?.can_play ||
                        music?.file ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Download</dt>
                            <dd className="text-gray-900">
                              <DownloadIcon
                                onClick={() => {
                                  downloadFile(
                                    music.title,
                                    music.file ?? music?.recording?.recording,
                                  );
                                }}
                                className={
                                  'h-5 w-5 cursor-pointer text-blue-500'
                                }
                              />
                            </dd>
                          </div>
                        ) : null}
                        {music?.can_play ||
                        music.preview ||
                        music?.recording ||
                        music?.file ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500">Play</dt>
                            <dd className="text-gray-900">
                              <PlayIcon
                                className={
                                  'h-5 w-5 cursor-pointer text-green-500'
                                }
                                onClick={() => {
                                  let track = {
                                    music: {
                                      id: music.id,
                                      image: music.smallAlbumCover ?? { icon },
                                      title: music.title,
                                      artist: music.artist,
                                      file:
                                        music?.file ??
                                        music?.recording?.recording ??
                                        music.preview,
                                      autoStart: true,
                                    },
                                  };
                                  setPlaying(track);
                                }}
                              />
                            </dd>
                          </div>
                        ) : null}

                        {music.can_edit ? (
                          <div className="flex justify-between py-3 text-sm font-medium">
                            <dt className="text-gray-500"></dt>
                            <dd className="text-gray-900">
                              <button
                                onClick={(e) => {
                                  e?.preventDefault();
                                  setMusic({ open: false });
                                  setLibraryItem({ track: music, open: true });
                                }}
                                className="bg-transparent hover:bg-blue-500 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded"
                              >
                                Edit
                              </button>
                            </dd>
                          </div>
                        ) : (
                          <></>
                        )}
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
